import { ConnectorStatusTypes } from '@common/enums';
import { useIDEParams } from '@common/ide/state';
import { useSelector } from '@common/performance/useSelector';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { LoadingDIcon, InfoCircleIcon } from '@prophecy/icons';
import { theme, BaseColor } from '@prophecy/theme';
import { Button, Ellipsis, RadialIconContainer, Stack, StackItem, Text, Tooltip, overlayStyle } from '@prophecy/ui-v3';
import { Tags } from '@prophecy/ui-v3/Tags';
import { capitalize } from 'lodash-es';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { format as timeAgoFormat } from 'timeago.js';

import { useConnectorEventsData, useObservationIDE } from '../hooks';
import { ConnectorEvent } from '../query';
import {
  ConnectorObservationMetadata,
  ObservationMetadata,
  ObservationState,
  ResetObservationKeyWord,
  actionTypes
} from '../redux/types';
import { ConnectorEventTypes } from '../types';
import { getFilteredConnectorEventsList, getIconDetailFromStatus, getLabelValue } from '../util';
import { ReadOnlyBanner } from './ReadonlyBanner';
import { StyledCard, StyledList, StyledStack, StyledStackItem, StyledTags } from './styled';

const SelectedConnectorTags = styled(Tags)`
  align-self: flex-start;
  position: absolute;
  margin-left: ${theme.spaces.x8};
`;

function ConnectorMonitoringDetail({ event }: { event: ConnectorEvent }) {
  const { icon, color } = getIconDetailFromStatus(event.status);
  const startTime = timeAgoFormat(event.created_at);

  function getMessage(event: ConnectorEvent) {
    switch (event.status) {
      case ConnectorStatusTypes.RETRY_WITH_ERROR:
      case ConnectorStatusTypes.FINISHED_WITH_ERROR:
        return event.error_message;
      case ConnectorStatusTypes.COMPLETED_SUCCESSFULLY:
        return 'Completed successfully';
      case ConnectorStatusTypes.RUNNING:
        return 'Running';
      case ConnectorStatusTypes.PENDING:
        return 'Pending';
    }
  }

  return (
    <StyledCard hasHoverEffect={false}>
      <Stack direction='horizontal' gap={theme.spaces.x20} width='100%' alignY='start' align='space-between'>
        <Tooltip title={`Status: ${capitalize(event.status)}`}>
          <Stack width='60px'>
            <RadialIconContainer tone={BaseColor.primary ?? color} gradient={true}>
              {icon}
            </RadialIconContainer>
          </Stack>
        </Tooltip>
        <StackItem grow='1'>
          <Stack gap={theme.spaces.x16}>
            <Ellipsis tooltip={true}>{getMessage(event)}</Ellipsis>
            <Stack direction='horizontal' gap={theme.spaces.x8}>
              <Ellipsis tooltipProps={{ overlayStyle }} tooltip={true}>
                {getLabelValue('Created', startTime)}
              </Ellipsis>
              {event.status === ConnectorStatusTypes.RETRY_WITH_ERROR && (
                <Ellipsis tooltipProps={{ overlayStyle }} tooltip={true}>
                  <Stack gap={theme.spaces.x4} direction='horizontal'>
                    <InfoCircleIcon type='default' color={theme.colors.gray500} />
                    <Text level='sm' tone={theme.colors.gray500}>
                      Will be retired soon.
                    </Text>
                  </Stack>
                </Ellipsis>
              )}
            </Stack>
          </Stack>
        </StackItem>
      </Stack>
    </StyledCard>
  );
}

export function ConnectorObservations() {
  const { isLoading: isLoadingConnectorEvents, loadMore, hasNextPage } = useConnectorEventsData();
  const { events } = useSelector<ObservationState, ConnectorObservationMetadata>((state) => state.connectorObservation);
  const { changeConnectorEvent } = useObservationIDE();
  const connectorObservation = useSelector<ObservationState, ConnectorObservationMetadata>(
    (state) => state.connectorObservation
  );
  const meta = useSelector<ObservationState, ObservationMetadata>((state) => state.observationMetadata);
  const { filteredEvents, eventCountMap } = getFilteredConnectorEventsList(events, connectorObservation.eventType);
  const eventsCount = events.length;
  const tableKey = connectorObservation.eventType + meta.startDateFrom;
  const dataSource = filteredEvents.map((rowData, i) => ({ rowData, key: i }));
  const dispatch = useDispatch();
  const isLoading = isLoadingConnectorEvents || meta.isLoadingMoreData;
  const { uid } = useIDEParams();
  const navigate = useNavigate();

  const getFilterButtonUI = (event: ConnectorEventTypes) => {
    const isSelected = connectorObservation.eventType === event;
    return (
      <StyledTags
        onClick={() => changeConnectorEvent(event)}
        icon={isLoading ? <LoadingDIcon type='extended' /> : null}
        tone={isSelected ? BaseColor.primary : BaseColor.gray}
        countCircle={true}
        size='l'
        count={eventCountMap[event]}
        data-test-id='connector-observation-filter-button'>
        {event}
      </StyledTags>
    );
  };

  return (
    <>
      <ReadOnlyBanner
        startDateFrom={meta.startDateFrom === ResetObservationKeyWord ? undefined : (meta.startDateFrom as number)}
        startDateTo={meta.startDateTo}
        handleDateChange={(date) => dispatch({ type: actionTypes.setObservationDate, payload: date })}
        loading={isLoading}
      />
      <StyledStack alignY='center' gap={theme.spaces.x8} align={'space-between'} width='100%' height='100%'>
        {uid && connectorObservation.selectedConnectorName && (
          <SelectedConnectorTags
            size='m'
            onClose={() => {
              dispatch({
                type: actionTypes.setSelectedConnectorName,
                payload: { connectorName: '' }
              });
              navigate(
                Private_Routes.IDE_Home.getUrl(
                  { entity: Entity.Observation.toLowerCase() },
                  { observedEntity: Entity.Connector }
                )
              );
            }}
            closable
            tone={BaseColor.primary}>
            {connectorObservation.selectedConnectorName}
          </SelectedConnectorTags>
        )}
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x6}>
          {getFilterButtonUI(ConnectorEventTypes.AttentionRequired)}
          {getFilterButtonUI(ConnectorEventTypes.All)}
        </Stack>
        <StyledStackItem grow='1'>
          <Stack
            alignY='top'
            width='100%'
            padding={`${theme.spaces.x10} 0 ${theme.spaces.x48}`}
            gap={theme.spaces.x20}
            height='100%'>
            <StyledList
              key={tableKey}
              virtualize={true}
              rowHeight={118}
              footer={() =>
                hasNextPage &&
                eventsCount > 0 && (
                  <Stack width='100%' alignY='center'>
                    <Button
                      loading={isLoading}
                      onClick={() => loadMore()}
                      size='s'
                      variant='secondaryGray'
                      data-test-id='connector-observation-load-more-button'>
                      Load More
                    </Button>
                  </Stack>
                )
              }
              loading={isLoading}
              emptyText={eventsCount < 1 && !isLoading ? 'No observation data.' : ''}
              data={dataSource}
              renderItem={(record, index) => (
                <ConnectorMonitoringDetail event={record.rowData as ConnectorEvent} key={index} />
              )}
              data-test-id='connector-observations-list'
            />
          </Stack>
        </StyledStackItem>
      </StyledStack>
    </>
  );
}
